import * as yup from "yup";

const csvTypeTest = (value: FileList | undefined) => {
  if (!value || !(value instanceof FileList) || value.length === 0)
    return false;
  const file = value[0];
  return file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
};

const csvSizeTest = (value: FileList | undefined) => {
  if (!value || !(value instanceof FileList) || value.length === 0)
    return false;
  const file = value[0];
  return file.size <= 5 * 1024 * 1024; // 5MB
};

export const tokenFormSchema = yup.object({
  tokenName: yup
    .string()
    .required(
      "Token Name is required. Please enter a valid name for your token."
    ),
  tokenDescription: yup
    .string()
    .required(
      "Token Description is required. Please provide a brief description of your token's purpose and use case."
    ),
  tokenSymbol: yup
    .string()
    .matches(/^[A-Z]{2,5}$/, "Enter a valid symbol (2-5 uppercase letters).")
    .required(
      "Token Symbol is required. Please enter a valid symbol (e.g., 2-5 uppercase letters)."
    ),
  telegram: yup
    .string()
    .test(
      "telegram-format",
      "Please enter a valid Telegram link or username (e.g., https://t.me/yourgroup or @username)",
      (value) => {
        if (!value) return true;
        return (
          /^https?:\/\/(t\.me|telegram\.me)\//.test(value) ||
          /^@[a-zA-Z0-9_]+$/.test(value) ||
          /^[a-zA-Z0-9_]+$/.test(value)
        );
      }
    )
    .notRequired(),
  twitter: yup
    .string()
    .test(
      "twitter-format",
      "Please enter a valid Twitter link or handle (e.g., https://twitter.com/yourhandle or @username)",
      (value) => {
        if (!value) return true;
        return (
          /^https?:\/\/(twitter\.com|x\.com)\//.test(value) ||
          /^@[a-zA-Z0-9_]+$/.test(value) ||
          /^[a-zA-Z0-9_]+$/.test(value)
        );
      }
    )
    .notRequired(),
  discord: yup
    .string()
    .test(
      "discord-format",
      "Please enter a valid Discord invite link (e.g., https://discord.gg/yourlink)",
      (value) => {
        if (!value) return true;
        return /^https?:\/\/(discord\.gg|discord\.com\/invite)\//.test(value);
      }
    )
    .notRequired(),
  website: yup
    .string()
    .url("Please enter a valid website URL (e.g., https://www.yourwebsite.com)")
    .notRequired(),
  image: yup
    .mixed<FileList>()
    .test(
      "required",
      "Please upload a valid image file.",
      (value) => {
        return value instanceof FileList && value.length > 0;
      }
    ),
  // Image preview URL (managed automatically)
  previewUrl: yup.string().notRequired(),
  // step 2
  vestingSchedule: yup.string().required("Vesting schedule is required."),
  marketCap: yup
    .number()
    .typeError("Market cap must be a number.")
    .required("Market cap is required."),
  restrictPresale: yup.boolean(),
  presaleVestingSchedule: yup.string().required("Presale schedule required."),

  //step 3
  enableCustomLaunch: yup.boolean().default(false),
  launchDate: yup
    .date()
    .nullable()
    .when("enableCustomLaunch", {
      is: true,
      then: (schema) =>
        schema
          .required("Please select a launch date and time.")
          .typeError("Please select a valid date and time."),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

  // Presale configuration (always available)
  presaleStartTime: yup
    .date()
    .nullable()
    .notRequired(),
  presaleEndTime: yup
    .date()
    .nullable()
    .test(
      "presale-end-after-start",
      "Presale end time must be after presale start time.",
      function (value) {
        const { presaleStartTime } = this.parent;
        if (!value || !presaleStartTime) return true;
        return new Date(value) > new Date(presaleStartTime);
      }
    )
    .notRequired(),

  // Allocation percentages - must sum to 80% per smart contract requirement
  presaleAllocationPercent: yup
    .number()
    .min(1, "Presale allocation must be at least 1%")
    .max(79, "Presale allocation cannot exceed 79%")
    .default(60)
    .test(
      "total-allocation",
      "Presale and Creator allocations must sum to exactly 80%",
      function (value) {
        const { creatorAllocationPercent } = this.parent;
        if (!value || !creatorAllocationPercent) return true;
        return value + creatorAllocationPercent === 80;
      }
    )
    .required("Presale allocation percentage is required."),
  creatorAllocationPercent: yup
    .number()
    .min(1, "Creator allocation must be at least 1%")
    .max(79, "Creator allocation cannot exceed 79%")
    .default(20)
    .test(
      "total-allocation",
      "Creator and Presale allocations must sum to exactly 80%",
      function (value) {
        const { presaleAllocationPercent } = this.parent;
        if (!value || !presaleAllocationPercent) return true;
        return value + presaleAllocationPercent === 80;
      }
    )
    .required("Creator allocation percentage is required."),
  userPresaleLimitPercent: yup
    .number()
    .min(1, "User presale limit must be at least 1%")
    .max(100, "User presale limit cannot exceed 100%")
    .default(50)
    .required("User presale limit percentage is required."),

  enableCreatorMod: yup.boolean().default(false),
  creatorName: yup.string().required("Creator name is required."),
  creatorSite: yup
    .string()
    .url("Enter a valid creator website URL.")
    .notRequired(),
  marketingPlan: yup.string().notRequired(),

  // Optional checkboxes (default to true unless user revokes)
  revokeFreeze: yup.boolean().notRequired(),
  revokeMint: yup.boolean().notRequired(),
  revokeUpdate: yup.boolean().notRequired(),
  whitelistCsv: yup.mixed<FileList>().when("restrictPresale", {
    is: true,
    then: (schema) =>
      schema
        .required("Please upload a CSV file with wallet addresses.")
        .test(
          "fileType",
          "Only CSV files are accepted for whitelisting",
          csvTypeTest
        )
        .test("fileSize", "File size must be less than 5MB", csvSizeTest),
    otherwise: (schema) =>
      schema
        .notRequired()
        .test(
          "fileType",
          "Only CSV files are accepted for whitelisting",
          (value) => {
            if (!value || value.length === 0) return true;
            return csvTypeTest(value);
          }
        )
        .test("fileSize", "File size must be less than 5MB", (value) => {
          if (!value || value.length === 0) return true;
          return csvSizeTest(value);
        }),
  }),
});

export type TokenFormData = yup.InferType<typeof tokenFormSchema>;
